import { useState } from "react";
import { toast } from "sonner";
import { Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/api";
import { useAuth } from "@/auth";
import { useLang, imgUrl } from "@/i18n";

export default function Profile() {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const isCoach = ["admin", "staff"].includes(user.role);
  const [form, setForm] = useState({
    name: user.name || "",
    phone: user.phone || "",
    picture: user.picture || "",
    years_experience: user.years_experience || 0,
    experience: user.experience || "",
  });
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/upload", fd);
      setForm((p) => ({ ...p, picture: r.data.path }));
      toast.success(t("saved"));
    } catch {
      toast.error(t("error"));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { name: form.name, phone: form.phone, picture: form.picture };
      if (isCoach) {
        payload.years_experience = Number(form.years_experience) || 0;
        payload.experience = form.experience;
      }
      const r = await api.put("/profile", payload);
      setUser(r.data);
      toast.success(t("saved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="profile-page">
      <h1 className="font-['Outfit'] text-2xl font-bold text-[#165A2E] md:text-3xl">{t("profile")}</h1>
      <form onSubmit={save} className="gfa-card max-w-2xl space-y-5 p-6">
        <div className="flex items-center gap-5">
          {form.picture ? (
            <img src={imgUrl(form.picture)} alt="" className="h-24 w-24 rounded-full border-4 border-[#84CC16]/40 object-cover" data-testid="profile-photo" />
          ) : (
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-[#165A2E]/10 font-['Outfit'] text-3xl font-bold text-[#165A2E]">{user.name?.[0]}</div>
          )}
          <div>
            <Label className="mb-2 block">{t("photo")}</Label>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-[#165A2E]/30 px-4 py-2 text-sm font-semibold text-[#165A2E] transition-colors duration-200 hover:bg-[#165A2E] hover:text-white">
              <Camera size={15} /> {t("uploadPhoto")}
              <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="profile-photo-input" />
            </label>
          </div>
        </div>
        <div>
          <Label>{t("name")}</Label>
          <Input data-testid="profile-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label>{t("phone")}</Label>
          <Input data-testid="profile-phone" placeholder="0123456789" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" />
        </div>
        {isCoach && (
          <>
            <div>
              <Label>{t("yearsExp")}</Label>
              <Input data-testid="profile-years" type="number" min="0" value={form.years_experience} onChange={(e) => setForm({ ...form, years_experience: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>{t("experience")}</Label>
              <Textarea data-testid="profile-experience" rows={4} placeholder="Cth: Jurulatih berlesen AFC C, bekas pemain Liga M3..." value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} className="mt-1" />
            </div>
          </>
        )}
        <Button data-testid="profile-save-btn" disabled={busy} className="rounded-full bg-[#165A2E] text-white hover:bg-[#114724]">
          {busy ? t("loading") : t("save")}
        </Button>
      </form>
    </div>
  );
}
